import React, { forwardRef, ReactNode } from "react";

type ButtonVariant = "primary" | "surface" | "neutral";
type ButtonSize = "md" | "sm";

type ButtonProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  "disabled"
> & {
  isLoading?: boolean;
  loader?: ReactNode;
  disabled?: boolean;
  variant?: ButtonVariant;
  size?: ButtonSize;
  fullWidth?: boolean;
};

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  primary: "bg-[#E6F0FB] text-[#004494]",
  surface: "bg-white text-[#004494]",
  neutral: "bg-[#E7E7E7] text-[#004494]",
};

const SIZE_CLASS: Record<ButtonSize, string> = {
  md: "h-[55px] min-h-[55px] px-6",
  sm: "h-11 min-h-11 px-5 text-[20px]",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      children,
      className,
      disabled = false,
      isLoading = false,
      loader,
      type = "button",
      variant = "primary",
      size = "md",
      fullWidth = true,
      onClick,
      ...rest
    },
    ref
  ) => {
    const isBlocked = disabled || isLoading;

    const handleClick: React.MouseEventHandler<HTMLButtonElement> = (event) => {
      if (isBlocked) {
        event.preventDefault();
        return;
      }
      onClick?.(event);
    };

    return (
      <button
        ref={ref}
        type={type}
        className={[
          "inline-flex items-center justify-center rounded-[30px] border-0",
          "font-[Roboto] text-[25px] font-bold leading-5",
          "[font-feature-settings:'liga'_off,'clig'_off]",
          "transition duration-200 hover:brightness-95",
          "disabled:cursor-not-allowed disabled:opacity-70",
          VARIANT_CLASS[variant],
          SIZE_CLASS[size],
          fullWidth ? "w-full" : "",
          className ?? "",
        ].join(" ")}
        onClick={handleClick}
        disabled={isBlocked}
        aria-busy={isLoading || undefined}
        {...rest}
      >
        {isLoading ? (
          loader ?? (
            <span
              className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
              aria-hidden
            />
          )
        ) : (
          children
        )}
      </button>
    );
  }
);

Button.displayName = "Button";
